import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { isNonProd } from '@/lib/appStage';

// ─── Layout ───────────────────────────────────────────────────────────────────
import { Layout } from '@/components/layout/Layout';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { CookieBanner } from '@/components/CookieBanner';
import { SessionWatcher } from '@/components/session/SessionWatcher';
import { ViewportProbe } from '@/components/dev/ViewportProbe';

// ─── Pages ────────────────────────────────────────────────────────────────────
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { CorporateAuthCallbackPage } from '@/pages/CorporateAuthCallbackPage';
import { DisclaimerPage } from '@/pages/DisclaimerPage';
import { OwnerAgreementRequiredPage } from '@/pages/OwnerAgreementRequiredPage';
import { PortalLoginPage } from '@/pages/PortalLoginPage';
import EFormPage from '@/pages/eform/EFormPage';
import UrlRedirectPage from '@/pages/eform/UrlRedirectPage';
import UnsubscribePage from '@/pages/email/UnsubscribePage';
import EmailViewPage from '@/pages/email/EmailViewPage';
import RequestInfoPage from '@/pages/request-info/RequestInfoPage';
import { REQUEST_INFO_ROUTE_PATH } from '@/pages/request-info/requestInfo.service';
import { DashboardPage } from '@/pages/DashboardPage';
import { PatientDetailRoute } from '@/pages/PatientDetailRoute';
import { AppointmentsPage } from '@/pages/AppointmentsPage';
import { PaymentsPage } from '@/pages/PaymentsPage';
import PaymentsCreditsPage from '@/pages/payments/PaymentsCreditsPage';
import PaymentsDashboardPage from '@/pages/payments/PaymentsDashboardPage';
import PaymentsAllocationsPage from '@/pages/payments/PaymentsAllocationsPage';
import AppOrdersPage from '@/pages/payments/AppOrdersPage';
import PayoutsPage from '@/pages/payments/PayoutsPage';
import DisputesPage from '@/pages/payments/DisputesPage';
import { TasksPage } from '@/pages/TasksPage';
import TasksDashboardPage from '@/pages/tasks/TasksDashboardPage';
import InboxPage from '@/pages/InboxPage';
import TaskRulesPage from '@/pages/tasks/TaskRulesPage';
import { DocumentsPage } from '@/pages/DocumentsPage';
import NewReportPage from '@/pages/reports/NewReportPage';
import ReportBuilderWizard from '@/pages/reports/ReportBuilderWizard';
import MyReportsPage from '@/pages/reports/MyReportsPage';
import ReportDetailPage from '@/pages/reports/ReportDetailPage';
import ReportResultViewer from '@/pages/reports/ReportResultViewer';
import StandardRunViewer from '@/pages/reports/StandardRunViewer';
import AiReportResultViewer from '@/pages/reports/AiReportResultViewer';
import AiReportScheduleViewer from '@/pages/reports/AiReportScheduleViewer';
import ReportPresetsPage from '@/pages/reports/ReportPresetsPage';
import StandardReportRunnerPage from '@/pages/reports/StandardReportRunnerPage';
import MarketingFlexTablePage from '@/pages/reports/flex/MarketingFlexTablePage';
import { FormsInboxPage } from '@/pages/forms/FormsInboxPage';
import { SurveyTemplatesPage } from '@/pages/surveys/SurveyTemplatesPage';
import { AdminPage } from '@/pages/AdminPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import SchedulePage from '@/pages/SchedulePage';
import PlansPage from '@/pages/PlansPage';
import PlanRedirect from '@/pages/plans/PlanRedirect';
import LeadsPage from '@/pages/LeadsPage';
import LeadDetailPage from '@/pages/leads/LeadDetailPage';
import NewLeadPage from '@/pages/leads/NewLeadPage';

function NewLeadPageKeyed() {
  const location = useLocation();
  return <NewLeadPage key={location.key} />;
}
import CRMDashboardPage from '@/pages/crm/CRMDashboardPage';
import InventoryDashboardPage from '@/pages/inventory/InventoryDashboardPage';
import InventoryPage from '@/pages/inventory/InventoryPage';
import VendorsPage from '@/pages/inventory/VendorsPage';
import PurchaseOrdersPage from '@/pages/inventory/PurchaseOrdersPage';
import InventoryReportsPage from '@/pages/inventory/InventoryReportsPage';
import GiftCardsPage from '@/pages/gift-cards/GiftCardsPage';
import MembershipsPage from '@/pages/MembershipsPage';
import MembershipsDashboardPage from '@/pages/memberships/MembershipsDashboardPage';
import CheckInPage from '@/pages/CheckInPage';
import TapLandingPage from '@/pages/TapLandingPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import { ScanCheckInPage } from '@/pages/ScanCheckInPage';
import LabOrdersPage from '@/pages/lab-orders/LabOrdersPage';
import SurgeryPage from '@/pages/surgery/SurgeryPage';
import ConsultsPage from '@/pages/consults/ConsultsPage';
import PeptidesPage from '@/pages/peptides/PeptidesPage';
import MarketplacePage from '@/pages/marketplace/MarketplacePage';
import MarketplaceOrdersPage from '@/pages/marketplace/MarketplaceOrdersPage';
// Lazy-loaded: the Vitaboom SDK pulls in MUI + Emotion (~a few hundred KB), so
// the orders page gets its own chunk that only Vitaboom-enabled accounts ever
// download (same treatment as the admin Marketplace page it was split from).
const VitaboomOrdersPage = lazyWithRetry(() => import('@/pages/vitaboom/VitaboomOrdersPage'), 'vitaboom-orders');
import { Spinner } from '@/components/ui';
import PartellPage from '@/pages/partell/PartellPage';
import RequireModule from '@/components/layout/RequireModule';
import RequirePermission from '@/components/layout/RequirePermission';
import { RequireDesktop } from '@/components/layout/RequireDesktop';
import { PERMISSION } from '@/lib/permissions';
import { SettingsPage } from '@/pages/SettingsPage';
import { PackagesPage } from '@/pages/PackagesPage';
import PackagesDashboardPage from '@/pages/packages/PackagesDashboardPage';
import NotificationsPage from '@/pages/notifications/NotificationsPage';
import ChatPage from '@/pages/messages/ChatPage';
import MessagesPage from '@/pages/messages/MessagesPage';
import CommunicationCenterOverviewPage from '@/pages/messages/CommunicationCenterOverviewPage';
import { VideoCallsPage } from '@/pages/VideoCallsPage';
import { VideoCallAnalyticsPage } from '@/pages/VideoCallAnalyticsPage';
import LoansPage from '@/pages/loans/LoansPage';
import CampaignsPage from '@/pages/campaigns/CampaignsPage';
import MarketingLayout from '@/pages/marketing/MarketingLayout';
import SmsCampaignsPage from '@/pages/marketing/SmsCampaignsPage';
import SmsCampaignEditorPage from '@/pages/marketing/SmsCampaignEditorPage';
import SmsCampaignScheduledPage from '@/pages/marketing/SmsCampaignScheduledPage';
import SmsCampaignContactsPage from '@/pages/marketing/SmsCampaignContactsPage';
import SmsCampaignExcludesPage from '@/pages/marketing/SmsCampaignExcludesPage';
import SmsCampaignSentContactsPage from '@/pages/marketing/SmsCampaignSentContactsPage';
import SmsCampaignStatsPage from '@/pages/marketing/SmsCampaignStatsPage';
import PushCampaignsPage from '@/pages/marketing/PushCampaignsPage';
import PushCampaignEditorPage from '@/pages/marketing/PushCampaignEditorPage';
import AdsDashboardPage from '@/pages/marketing/AdsDashboardPage';
import SocialMediaPage from '@/pages/marketing/SocialMediaPage';
import MarketingExplorerPage from '@/pages/marketing/MarketingExplorerPage';
import AutomationsPage from '@/pages/marketing/AutomationsPage';
import EmailAutomationEditorPage from '@/pages/marketing/EmailAutomationEditorPage';
import EmailAutomationStatsPage from '@/pages/marketing/EmailAutomationStatsPage';
import EmailAutomationContactsPage from '@/pages/marketing/EmailAutomationContactsPage';
import SmsAutomationEditorPage from '@/pages/marketing/SmsAutomationEditorPage';
import SmsAutomationStatsPage from '@/pages/marketing/SmsAutomationStatsPage';
import SmsAutomationContactsPage from '@/pages/marketing/SmsAutomationContactsPage';
import TemplatesPage from '@/pages/marketing/TemplatesPage';
import { ContactListsPage, ContactListDetailPage, ContactListContactsPage } from '@/pages/marketing/contact-lists';
// AnalyticsPage removed — replaced by ReportsPage
import MarketingSettingsPage from '@/pages/marketing/MarketingSettingsPage';
import AppPostsPage from '@/pages/marketing/AppPostsPage';
import AppleTvGetStartedPage from '@/pages/marketing/apple-tv/AppleTvGetStartedPage';
import GoogleBusinessPage from '@/pages/marketing/google-business/GoogleBusinessPage';
import AppleTvLocationsPage from '@/pages/marketing/apple-tv/AppleTvLocationsPage';
import AppleTvScreensPage from '@/pages/marketing/apple-tv/AppleTvScreensPage';
import AppleTvPlaylistsPage from '@/pages/marketing/apple-tv/AppleTvPlaylistsPage';
import AppleTvMediaPage from '@/pages/marketing/apple-tv/AppleTvMediaPage';
import AppActivityPage from '@/pages/marketing/AppActivityPage';
import SmsTemplateBuilderPage from '@/pages/marketing/SmsTemplateBuilderPage';
import EmailTemplateBuilderPage from '@/pages/marketing/EmailTemplateBuilderPage';
import RequestInfoLinkBuilderPage from '@/pages/marketing/RequestInfoLinkBuilderPage';
import EmailCampaignEditorPage from '@/pages/marketing/EmailCampaignEditorPage';
import EmailCampaignScheduledPage from '@/pages/marketing/EmailCampaignScheduledPage';
import EmailCampaignContactsPage from '@/pages/marketing/EmailCampaignContactsPage';
import EmailCampaignExcludesPage from '@/pages/marketing/EmailCampaignExcludesPage';
import EmailCampaignsPage from '@/pages/marketing/EmailCampaignsPage';
import EmailCampaignStatsPage from '@/pages/marketing/EmailCampaignStatsPage';
import MarketingDashboardPage from '@/pages/marketing/MarketingDashboardPage';
import PhotoLibraryPage from '@/pages/marketing/PhotoLibraryPage';
import MarketingGuidesPage from '@/pages/marketing/MarketingGuidesPage';
import VideoLibraryPage from '@/pages/marketing/VideoLibraryPage';
import MarketingReportsPage from '@/pages/marketing/ReportsPage';
import QaPage from '@/pages/qa/QaPage';
import PostOpsPage from '@/pages/post-ops/PostOpsPage';
import { VideoJoinPage } from '@/pages/VideoJoinPage';
import SurveysDashboardPage from '@/pages/surveys/SurveysDashboardPage';
import { SurveyBuilderPage } from '@/pages/surveys/SurveyBuilderPage';
import SurveyResponsesPage from '@/pages/surveys/SurveyResponsesPage';
import { HelpPage } from '@/pages/HelpPage';
import { PrescribingPage } from '@/pages/PrescribingPage';
import AIAssistantPage from '@/pages/ai-assistant/AIAssistantPage';
import { SupportTicketDetailPage } from '@/pages/help/SupportTicketDetailPage';
import PatientProductTypesDebugPage from '@/pages/debug/PatientProductTypesDebugPage';
// ─── V5 pages (co-worker's UI — replaces V1 where available) ────────────────
import CRMPipelinePage from '@/v5/pages/CRMPage';
import { PatientsPage } from '@/pages/PatientsPage';

// ─── Knowledge Base (staff-facing) ───────────────────────────────────────────
import { MyTrainingPage } from '@/pages/knowledge/MyTrainingPage';
import { KnowledgeLibraryPage } from '@/pages/knowledge/KnowledgeLibraryPage';
import { KnowledgeArticlePage } from '@/pages/knowledge/KnowledgeArticlePage';
import { MyAnnouncementsPage } from '@/pages/knowledge/MyAnnouncementsPage';

// ─── Announcements (staff-facing archive) ────────────────────────────────────
import { AnnouncementsArchivePage } from '@/pages/AnnouncementsArchivePage';

// ─── Auth guard ───────────────────────────────────────────────────────────────

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const pendingDisclaimers = useAuthStore((s) => s.pendingDisclaimers);
  const ownerAgreementHold = useAuthStore((s) => s.ownerAgreementHold);
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // First-login disclaimer gate. Disclaimer rows are per-account; users in
  // accounts that haven't configured one will have an empty queue and pass
  // straight through.
  if (pendingDisclaimers.length > 0 && location.pathname !== '/disclaimer') {
    return <Navigate to="/disclaimer" state={{ from: location }} replace />;
  }

  // The practice is waiting for its first account owner to accept the owner
  // agreement. Checked after the disclaimer queue so an owner, who is never
  // held, still reaches the agreement they are here to sign.
  if (ownerAgreementHold && location.pathname !== '/owner-agreement-required') {
    return <Navigate to="/owner-agreement-required" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// ─── Legacy report-builder links ──────────────────────────────────────────────

/** /reports/builder/:orderId → the single order edit URL, preserving the id. */
/**
 * What the report builder offers a narrow viewport instead of its canvas (SCRUM-1472).
 * Shared by all four wizard entries below.
 */
const REPORT_BUILDER_GATE = {
  what: 'The report builder',
  alternative: { label: 'View saved reports', to: '/reports/my-reports' },
} as const;

function RedirectToOrderEdit() {
  const { orderId } = useParams<{ orderId: string }>();
  return <Navigate to={`/reports/orders/${orderId}/edit`} replace />;
}

// ─── Router ───────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/callback" element={<CorporateAuthCallbackPage />} />
        <Route path="/portal/login" element={<PortalLoginPage />} />
        <Route path="/checkin" element={<CheckInPage />} />
        {/* Front-desk tap pad link. With the Next Health app installed the OS
            hands this URL to the app (Universal Link); this page is the
            fallback for phones without it. Public and static: no API call. */}
        <Route path="/tap/:deskToken" element={<TapLandingPage />} />
        <Route path="/video-join" element={<VideoJoinPage />} />
        <Route path="/eform" element={<EFormPage />} />
        <Route path="/url" element={<UrlRedirectPage />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        {/* Privacy policy registered on the Google OAuth consent screen. */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/email/view" element={<EmailViewPage />} />
        {/* Marketing request-info landing page: /l/<code> for a bare link,
            /l/<code>/<suffix> once a send has personalized it (a trailing slash
            with nothing after it is still a bare link). Public by design — the
            recipient taps one button with no login. */}
        <Route path={REQUEST_INFO_ROUTE_PATH} element={<RequestInfoPage />} />

        {/* Wallet membership-card NFC/QR scan → resolve → redirect to the
            patient's appointments. Authenticated, full-screen redirector. */}
        <Route
          path="/scan/:token"
          element={
            <RequireAuth>
              <ScanCheckInPage />
            </RequireAuth>
          }
        />

        {/* First-login disclaimer gate — authenticated but rendered full-screen
            outside the app Layout. */}
        <Route
          path="/disclaimer"
          element={
            <RequireAuth>
              <DisclaimerPage />
            </RequireAuth>
          }
        />

        <Route
          path="/owner-agreement-required"
          element={
            <RequireAuth>
              <OwnerAgreementRequiredPage />
            </RequireAuth>
          }
        />

        {/* Protected routes — wrapped in Layout */}
        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          {/* Per-area module gates (config/module_gates.php mirrors these on
              the API). Accounts without module rows pass every gate — only
              explicitly opted-out (restricted) accounts are redirected, to
              their safe landing path. Patients stays ungated by design. */}
          <Route element={<RequireModule moduleKey="dashboard" />}>
            <Route index element={<DashboardPage />} />
            <Route path="inbox" element={<InboxPage />} />
          </Route>
          <Route path="patients" element={<PatientsPage />} />
          {/* PatientDetailRoute renders PatientDetailPage, or the online gift card
              sales page for that placeholder patient. */}
          <Route path="patients/:id" element={<PatientDetailRoute />} />
          <Route path="patients/:id/plans/:planId?" element={<PatientDetailRoute />} />
          {/* Patient intake is CORE (the Patients page's "Add Patient" opens it)
              — deliberately outside the crm gate so restricted accounts can
              register patients. The leads LIST/pipeline stay crm-gated. */}
          <Route path="leads/new" element={<NewLeadPageKeyed />} />
          <Route element={<RequireModule moduleKey="scheduling" />}>
            <Route path="appointments" element={<AppointmentsPage />} />
            <Route path="schedule" element={<SchedulePage />} />
          </Route>
          <Route element={<RequireModule moduleKey="payments" />}>
            <Route path="payments" element={<PaymentsDashboardPage />} />
            <Route path="payments/all" element={<PaymentsPage />} />
            <Route path="payments/credits" element={<PaymentsCreditsPage />} />
            {/* Gated by `payments` alone (the group above), like All Payments
                and Credits — it has no module of its own. */}
            <Route path="payments/app-orders" element={<AppOrdersPage />} />
            {/* Payouts and Disputes carry a permission each, not the admin level
                they briefly sat behind. Two separate wrappers, not one: a user
                may reconcile deposits without answering chargebacks, and
                RequirePermission takes a single id.

                The fallback is /payments rather than the default safe landing,
                so following an old link without the grant leaves you inside
                Payments instead of bounced out of the section entirely. */}
            <Route element={<RequirePermission permissionId={PERMISSION.PAYOUTS} fallback="/payments" />}>
              <Route path="payments/payouts" element={<PayoutsPage />} />
            </Route>
            <Route element={<RequirePermission permissionId={PERMISSION.DISPUTES} fallback="/payments" />}>
              <Route path="payments/disputes" element={<DisputesPage />} />
            </Route>
            {/* The admin paths these two lived at for one day, so anything
                bookmarked in that window still resolves. */}
            <Route path="admin/payments/forward/payouts" element={<Navigate to="/payments/payouts" replace />} />
            <Route path="admin/payments/forward/disputes" element={<Navigate to="/payments/disputes" replace />} />
            {/* Allocations is its own module, nested inside payments: it needs
                BOTH — a practice without payments has no allocations to report
                on, and one with payments may still not want the report. */}
            <Route element={<RequireModule moduleKey="allocations" />}>
              <Route path="payments/allocations" element={<PaymentsAllocationsPage />} />
            </Route>
            <Route path="gift-cards" element={<GiftCardsPage />} />
            <Route path="loans" element={<LoansPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="tasks" />}>
            <Route path="tasks" element={<TasksDashboardPage />} />
            <Route path="tasks/rules" element={<TaskRulesPage />} />
            <Route path="tasks/all" element={<TasksPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="forms" />}>
            <Route path="documents" element={<DocumentsPage />} />
            <Route path="surveys" element={<SurveysDashboardPage />} />
            <Route path="surveys/all" element={<FormsInboxPage />} />
            <Route path="surveys/templates" element={<SurveyTemplatesPage />} />
            <Route
              path="surveys/:id/build"
              element={
                <RequireDesktop what="The survey builder">
                  <SurveyBuilderPage />
                </RequireDesktop>
              }
            />
            <Route path="surveys/:surveyId/responses" element={<SurveyResponsesPage />} />
          </Route>
          {/* Custom Reports (CDM/CST) */}
          <Route element={<RequireModule moduleKey="reports" />}>
            {/* ── Open to any user of the reports module ──────────────────────
                Reports permission 15 gates this section PARTIALLY. These pages
                stay open so a user without it can still open reports they own or
                that were shared with them, plus every run underneath them — and
                the viewers those pages link into, which would otherwise be dead
                ends. The AI viewers carry their own permission (26). */}
            <Route path="reports/my-reports" element={<MyReportsPage />} />
            <Route path="reports/my-reports/:kind/:id" element={<ReportDetailPage />} />
            <Route path="reports/results/:resultId" element={<ReportResultViewer />} />
            {/* A finished standard run. Belongs HERE and not with the runner
                below: My Reports lists a bare catalog run as a row of its own,
                and a viewer behind permission 15 would make that row a click
                that bounces straight back to the list. */}
            <Route path="reports/standard-runs/:runId" element={<StandardRunViewer />} />
            <Route path="reports/ai-results/:id" element={<AiReportResultViewer />} />
            <Route path="reports/ai-schedules/:id" element={<AiReportScheduleViewer />} />

            {/* ── Removed sections ────────────────────────────────────────────
                Scheduled Reports, History and Manage are gone: each listed a
                different slice of the same reports with a different subset of the
                same actions, and nothing on screen said which slice you had. My
                Reports is now the single list, with each report's runs under it.

                Redirected rather than deleted because staff bookmark these and a
                404 gives them nowhere to go. `replace` so Back does not bounce
                them straight into the redirect again. */}
            <Route path="reports/orders" element={<Navigate to="/reports/my-reports" replace />} />
            <Route path="reports/history" element={<Navigate to="/reports/my-reports" replace />} />

            {/* ── Behind Reports permission 15 ────────────────────────────────
                Everything that builds, schedules or runs a report. Grouped here
                rather than wrapped individually so the boundary is one line to
                read and cannot drift entry by entry.

                `fallback` is the nearer parent, not the app landing page: a
                blocked user still belongs in this section, so they land on My
                Reports rather than being thrown to the dashboard.

                This guards the URL, not the data — the API enforces no
                permission today, so these endpoints remain reachable outside the
                app. Tracked separately. */}
            <Route element={<RequirePermission permissionId={PERMISSION.REPORTS} fallback="/reports/my-reports" />}>
              {/* The single ordering entry point: pre-built catalog, then build
                  your own. This route used to render a grid of hardcoded tiles
                  that no nav entry pointed at — reachable only by cancelling out
                  of the builder, which still navigates here. */}
              <Route path="reports" element={<NewReportPage />} />
              {/* The catalog folded into the page above. */}
              <Route path="reports/standard" element={<Navigate to="/reports" replace />} />
              {/* A catalog entry with its own live page; the catalog card links here. */}
              <Route path="reports/marketing-flex-table" element={<MarketingFlexTablePage />} />
              {/* The runner takes the report KEY, not an id: the catalog is
                  generated from server-side definitions, so a new report becomes
                  reachable without a route change. */}
              <Route path="reports/standard/:reportKey" element={<StandardReportRunnerPage />} />

              {/* The builder canvas cannot work below lg, so every entry that renders
                  the wizard is gated where it sits rather than reordering the route
                  table. reports/builder/:orderId below is a redirect, not the wizard,
                  so it stays ungated — gating it would show the notice instead of
                  forwarding.

                  SCRUM-1472 confirmed the gate itself is intended and asked for a way
                  out other than "Go back", which is a dead end. /reports/my-reports is
                  ungated (declared above, outside this block), renders through the
                  shared DataTable so it is in card mode below md, and is already this
                  section's route fallback — so a phone user can still open and run a
                  saved report. One constant for all four entries, so they cannot drift. */}
              <Route
                path="reports/builder/new"
                element={
                  <RequireDesktop {...REPORT_BUILDER_GATE}>
                    <ReportBuilderWizard />
                  </RequireDesktop>
                }
              />
              {/* One URL edits one order. This used to be a second, equivalent edit
                  route, and "Use as on-demand" pointed at it with a reports_cdm_orders
                  id — which the wizard then looked up in reports_cst_orders, either
                  404ing or hydrating (and on save overwriting) an unrelated order that
                  happened to share the number. Nothing mints those links now; the
                  redirect keeps existing bookmarks landing on the real edit page.
                  Note an old bookmark's id is still ambiguous between the two tables —
                  the id alone cannot say which it came from. */}
              <Route
                path="reports/builder/:orderId"
                element={<RedirectToOrderEdit />}
              />
              <Route
                path="reports/orders/:orderId/edit"
                element={
                  <RequireDesktop {...REPORT_BUILDER_GATE}>
                    <ReportBuilderWizard />
                  </RequireDesktop>
                }
              />
              <Route path="reports/presets" element={<ReportPresetsPage />} />
              {/* Same wizard, preset modes — see ReportBuilderWizard's mode resolution.
                  `edit` changes the template; `use` seeds a new order from it. */}
              <Route
                path="reports/presets/:presetId/edit"
                element={
                  <RequireDesktop {...REPORT_BUILDER_GATE}>
                    <ReportBuilderWizard />
                  </RequireDesktop>
                }
              />
              <Route
                path="reports/presets/:presetId/use"
                element={
                  <RequireDesktop {...REPORT_BUILDER_GATE}>
                    <ReportBuilderWizard />
                  </RequireDesktop>
                }
              />
            </Route>
          </Route>
          <Route element={<RequireModule moduleKey="crm" />}>
            <Route path="plans" element={<PlansPage />} />
            <Route path="plans/:planId" element={<PlanRedirect />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="leads/:leadId" element={<LeadDetailPage />} />
            <Route path="crm" element={<CRMDashboardPage />} />
            <Route path="crm/pipeline" element={<CRMPipelinePage />} />
          </Route>
          <Route element={<RequireModule moduleKey="inventory" />}>
            <Route path="inventory" element={<InventoryDashboardPage />} />
            <Route path="inventory/stock" element={<InventoryPage />} />
            <Route path="inventory/vendors" element={<VendorsPage />} />
            <Route path="inventory/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="inventory/reports" element={<InventoryReportsPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="memberships" />}>
            <Route path="memberships" element={<MembershipsDashboardPage />} />
            <Route path="memberships/all" element={<MembershipsPage />} />
            <Route path="memberships/members" element={<Navigate to="/memberships/all?tab=members" replace />} />
          </Route>
          <Route element={<RequireModule moduleKey="packages" />}>
            <Route path="packages" element={<PackagesDashboardPage />} />
            <Route path="packages/all" element={<PackagesPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="alerts" />}>
            <Route path="alerts" element={<NotificationsPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="messaging" />}>
            <Route path="messages" element={<MessagesPage />} />
            <Route path="messages/overview" element={<CommunicationCenterOverviewPage />} />
            <Route path="messages/chat" element={<ChatPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="video" />}>
            <Route path="video-calls" element={<VideoCallsPage />} />
            <Route path="video-calls/analytics" element={<VideoCallAnalyticsPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="marketing" />}>
            <Route element={<RequirePermission permissionId={PERMISSION.MARKETING_CAMPAIGNS} />}>
              <Route path="campaigns" element={<CampaignsPage />} />
            </Route>
          </Route>

          {/* Marketing section — every surface under it requires Marketing
              Access (20); the sub-permissions below narrow it further. */}
          <Route element={<RequireModule moduleKey="marketing" />}>
          <Route element={<RequirePermission permissionId={PERMISSION.MARKETING} />}>
          <Route path="marketing" element={<MarketingLayout />}>
            <Route index element={<MarketingDashboardPage />} />
            {/* Campaign building — permission 27. Blocked staff fall back to the
                marketing dashboard, which Marketing Access alone still allows. */}
            <Route element={<RequirePermission permissionId={PERMISSION.MARKETING_CAMPAIGNS} fallback="/marketing" />}>
              <Route path="email-campaigns" element={<EmailCampaignsPage />} />
              <Route path="sms-campaigns" element={<SmsCampaignsPage />} />
              <Route path="sms-campaigns/:id" element={<SmsCampaignEditorPage />} />
              <Route path="sms-campaigns/:id/scheduled" element={<SmsCampaignScheduledPage />} />
              <Route path="sms-campaigns/:id/contacts" element={<SmsCampaignContactsPage />} />
              <Route path="sms-campaigns/:id/stats" element={<SmsCampaignStatsPage />} />
              <Route path="sms-campaigns/:id/sent-contacts" element={<SmsCampaignSentContactsPage />} />
              <Route path="sms-campaigns/:id/excludes" element={<SmsCampaignExcludesPage />} />
              <Route path="campaigns/:id" element={<EmailCampaignEditorPage />} />
              <Route path="campaigns/:id/scheduled" element={<EmailCampaignScheduledPage />} />
              <Route path="campaigns/:id/stats" element={<EmailCampaignStatsPage />} />
              <Route path="campaigns/:id/contacts" element={<EmailCampaignContactsPage />} />
              <Route path="campaigns/:id/excludes" element={<EmailCampaignExcludesPage />} />
              <Route element={<RequireModule moduleKey="zernio" requireActive notFound />}>
                <Route path="social" element={<SocialMediaPage />} />
              </Route>
            </Route>
            {/* Push notifications — permission 29 */}
            <Route element={<RequirePermission permissionId={PERMISSION.MARKETING_PUSH} fallback="/marketing" />}>
              <Route path="push-campaigns" element={<PushCampaignsPage />} />
              <Route path="push-campaigns/:id" element={<PushCampaignEditorPage />} />
            </Route>
            <Route path="ads" element={<AdsDashboardPage />} />
            <Route path="ai-explorer" element={<MarketingExplorerPage />} />
            {/* Automations — permission 28 */}
            <Route element={<RequirePermission permissionId={PERMISSION.MARKETING_AUTOMATIONS} fallback="/marketing" />}>
              <Route path="automations" element={<AutomationsPage />} />
              <Route path="automations/email/:id" element={<EmailAutomationEditorPage />} />
              <Route path="automations/email/:id/stats" element={<EmailAutomationStatsPage />} />
              <Route path="automations/email/:id/contacts" element={<EmailAutomationContactsPage />} />
              <Route path="automations/sms/:id" element={<SmsAutomationEditorPage />} />
              <Route path="automations/sms/:id/stats" element={<SmsAutomationStatsPage />} />
              <Route path="automations/sms/:id/contacts" element={<SmsAutomationContactsPage />} />
            </Route>
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/sms/:id" element={<SmsTemplateBuilderPage />} />
            <Route path="templates/email/:id" element={<EmailTemplateBuilderPage />} />
            <Route path="templates/links/:id" element={<RequestInfoLinkBuilderPage />} />
            <Route path="contact-lists" element={<ContactListsPage />} />
            <Route path="contact-lists/:listId" element={<ContactListContactsPage />} />
            <Route path="contact-lists/:listId/details" element={<ContactListDetailPage />} />
            <Route path="photos" element={<PhotoLibraryPage />} />
            <Route path="videos" element={<VideoLibraryPage />} />
            <Route path="reports" element={<MarketingReportsPage />} />
            {/* App posts — permission 30 */}
            <Route element={<RequirePermission permissionId={PERMISSION.MARKETING_APP_POSTS} fallback="/marketing" />}>
              <Route path="posts" element={<AppPostsPage />} />
              <Route path="app-activity" element={<AppActivityPage />} />
              <Route path="blog" element={<Navigate to="/marketing/posts" replace />} />
              <Route path="blog-config" element={<Navigate to="/marketing/posts?tab=settings" replace />} />
            </Route>
            {/* Marketing settings — permission 31 */}
            <Route element={<RequirePermission permissionId={PERMISSION.MARKETING_SETTINGS} fallback="/marketing" />}>
              <Route path="settings" element={<MarketingSettingsPage />} />
            </Route>
            {/* Apple TV signage — permission 32, and the signage module must be
                explicitly active. requireActive matches the nav's fail-CLOSED
                gate; notFound so a practice without signage sees nothing rather
                than being bounced somewhere unexplained. */}
            {/* Google Business rides on the account's Zernio connection for its
                data, but carries its OWN module gate: a practice can be sold
                review management without social publishing. Without a saved
                Zernio key the section still loads and explains itself, rather
                than 404ing here. */}
            <Route element={<RequireModule moduleKey="google business" requireActive notFound />}>
              {/* Three routes, one component: the section shares its connection
                  status and chosen location across all three views, so splitting
                  it into separate pages would mean re-fetching both on every
                  switch. The view is derived from the path instead. */}
              <Route
                path="google-business"
                element={<Navigate to="/marketing/google-business/locations" replace />}
              />
              <Route path="google-business/locations" element={<GoogleBusinessPage />} />
              <Route path="google-business/overview" element={<GoogleBusinessPage />} />
              <Route path="google-business/reviews" element={<GoogleBusinessPage />} />
            </Route>

            <Route element={<RequireModule moduleKey="franchisesign" requireActive notFound />}>
              <Route element={<RequirePermission permissionId={PERMISSION.APPLE_TV} fallback="/marketing" />}>
                <Route path="apple-tv" element={<Navigate to="/marketing/apple-tv/get-started" replace />} />
                <Route path="apple-tv/get-started" element={<AppleTvGetStartedPage />} />
                <Route path="apple-tv/locations" element={<AppleTvLocationsPage />} />
                <Route path="apple-tv/screens" element={<AppleTvScreensPage />} />
                <Route path="apple-tv/playlists" element={<AppleTvPlaylistsPage />} />
                <Route path="apple-tv/media" element={<AppleTvMediaPage />} />
              </Route>
            </Route>
            <Route element={<RequireModule moduleKey="marketing guides" requireActive notFound />}>
              <Route path="guides" element={<MarketingGuidesPage />} />
            </Route>
          </Route>
          </Route>
          </Route>
          {/* Surgery is opt-in AND permission-gated: requireActive is the
              fail-CLOSED module half (the middleware path map is fail-open and
              cannot express default-OFF), and SurgeryController additionally
              requires legacy permission 6. notFound so the area looks absent to
              a practice that does not have it, rather than bouncing staff.
              RequirePermission is the route half of permission 6, matching the
              nav entry's requiredPermission, so a user without it who follows
              an old link lands somewhere they can use instead of on the list. */}
          <Route element={<RequireModule moduleKey="surgery" requireActive notFound />}>
            <Route element={<RequirePermission permissionId={PERMISSION.SURGERY} />}>
              <Route path="surgery" element={<SurgeryPage />} />
            </Route>
          </Route>
          {/* Consults: opt-in AND permission-gated, like Surgery and Post-ops.
              requireActive is the fail-CLOSED module half; notFound so the area
              reads as absent to a practice without it. RequirePermission is the
              route half of permission 5, matching the nav entry. */}
          <Route element={<RequireModule moduleKey="consults" requireActive notFound />}>
            <Route element={<RequirePermission permissionId={PERMISSION.CONSULTS} />}>
              <Route path="consults" element={<ConsultsPage />} />
            </Route>
          </Route>
          <Route element={<RequireModule moduleKey="qa" />}>
            <Route path="qa" element={<QaPage />} />
          </Route>
          {/* requireActive + notFound: the area reads as absent for an
              account without the module, rather than bouncing staff. The
              permission is a second, independent gate — the nav entry checks
              both, so a typed URL must too. */}
          <Route element={<RequireModule moduleKey="post ops" requireActive notFound />}>
            <Route element={<RequirePermission permissionId={PERMISSION.POST_OPS} />}>
              <Route path="post-ops" element={<PostOpsPage />} />
            </Route>
          </Route>
          <Route element={<RequireModule moduleKey="labs" />}>
            <Route path="lab-orders" element={<LabOrdersPage />} />
          </Route>
          <Route element={<RequireModule moduleKey="axiom" />}>
            <Route path="peptides" element={<PeptidesPage />} />
          </Route>
          {/* Vitaboom orders — fail-CLOSED like the /admin/marketplace route it
              was split from: the marketplace is opt-in, so it requires an
              explicitly ACTIVE 'vitaboom' module row. */}
          <Route element={<RequireModule moduleKey="vitaboom" requireActive />}>
            <Route
              path="vitaboom"
              element={(
                <Suspense fallback={<div className="flex justify-center py-16"><Spinner /></div>}>
                  <VitaboomOrdersPage />
                </Suspense>
              )}
            />
          </Route>
          {/* Marketplace → Partell. requireActive matches the nav entry's
              fail-CLOSED gate: Partell is an opt-in module. */}
          <Route element={<RequireModule moduleKey="partell" requireActive />}>
            <Route path="partell" element={<PartellPage />} />
          </Route>
          {/* No module gate on the page itself: it lists only the apps whose
              modules are on, and says so when there are none. */}
          <Route path="marketplace" element={<MarketplacePage />} />
          {/* Same: it lists only the ready apps' orders, and needs user level 2
              like the vendor order pages (it says so below that). */}
          <Route path="marketplace/orders" element={<MarketplaceOrdersPage />} />
          {/* ScriptSure's practice-level screens. requireActive matches the
              nav entry's fail-CLOSED gate; notFound so a practice that does
              not prescribe through ScriptSure sees no such page rather than
              being bounced somewhere unexplained. */}
          <Route element={<RequireModule moduleKey="daw" requireActive notFound />}>
            <Route path="prescribing" element={<PrescribingPage />} />
          </Route>
          <Route path="help" element={<HelpPage />} />
          <Route path="help/tickets/:ticketId" element={<SupportTicketDetailPage />} />

          {/* Staff-facing Knowledge Base */}
          <Route path="knowledge" element={<MyTrainingPage />} />
          <Route path="knowledge/library" element={<KnowledgeLibraryPage />} />
          <Route path="knowledge/article/:id" element={<KnowledgeArticlePage />} />
          <Route path="knowledge/announcements" element={<MyAnnouncementsPage />} />

          {/* Announcements archive — Prospera announcements (active + past/closed) */}
          <Route path="announcements" element={<AnnouncementsArchivePage />} />

          {/* AI Assistant — staging only (and local dev), hidden in production;
              module-gated per account like every other area. */}
          {isNonProd() && (
            <Route element={<RequireModule moduleKey="ai assistant" />}>
              <Route path="ai-assistant" element={<AIAssistantPage />} />
            </Route>
          )}

          {/* Debug */}
          <Route path="debug/patient-product-types" element={<PatientProductTypesDebugPage />} />

          {/* Admin section uses nested Routes internally */}
          <Route path="admin/*" element={<AdminPage />} />
          <Route path="settings" element={<SettingsPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <CookieBanner />
      {/* Idle auto-logout + the single sign-out redirect. Mounted here rather
          than in Layout so it also covers /disclaimer and /scan/:token, which
          are authenticated but render outside it. */}
      <SessionWatcher />
      {/* Inert unless `?vvdebug=1` is in the URL — see the component's docblock for
          what it measures and why the Web Inspector version of it was never run. */}
      <ViewportProbe />
    </BrowserRouter>
  );
}
